// per docs/shape-uc-30-sites.md §6.4 — a searchable single-select, because
// ACCS-09 makes any selector that can pass ten entries searchable, MUD has no
// combobox, and wireframes/00-decisions.md D4 says compose one from what exists.
// It stays local to UC30 until a second screen needs it (PRINCIPLES rule of three).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudTextInput } from '@egov-moldova/mud-react';

export interface SearchSelectOption {
  value: string;
  label: string;
  meta?: string;
}

interface SearchSelectProps {
  value: string;
  options: SearchSelectOption[];
  placeholder: string;
  ariaLabel: string;
  label?: string;
  invalid?: boolean;
  disabled?: boolean;
  // True when the list this picker chooses from could not be read. Without it an
  // empty panel says "no match" whatever the reason, so a Registrar list that
  // failed to load told the administrator there were no Registrars.
  optionsUnavailable?: boolean;
  onPick: (value: string) => void;
}

// Rendering the whole geographic nomenclator into one panel would be 1 700 rows
// of DOM per keystroke. ACCS-09 asks for searchable and paginated; this is the
// page, and the panel says how many more the query still matches.
const VISIBLE_MATCHES = 40;

function comparable(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

export function SearchSelect({
  value,
  options,
  placeholder,
  ariaLabel,
  label,
  invalid,
  disabled,
  optionsUnavailable = false,
  onPick,
}: SearchSelectProps): JSX.Element {
  const { t } = useTranslate();
  const selected = options.find((option) => option.value === value);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onPointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const matches = useMemo(() => {
    const needle = comparable(query.trim());
    if (needle.length === 0) return options;
    return options.filter((option) =>
      comparable(option.label + ' ' + (option.meta ?? '')).includes(needle),
    );
  }, [options, query]);

  const shown = matches.slice(0, VISIBLE_MATCHES);
  const overflow = matches.length - shown.length;

  function choose(option: SearchSelectOption): void {
    onPick(option.value);
    setQuery('');
    setOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => {
        if (shown.length === 0) return 0;
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        return (next + shown.length) % shown.length;
      });
      return;
    }
    if (event.key === 'Enter') {
      const option = shown[highlight];
      if (open && option) {
        event.preventDefault();
        choose(option);
      }
    }
  }

  return (
    <div className="simsm-sites__picker" ref={rootRef} onKeyDown={handleKeyDown}>
      <MudTextInput
        size="md"
        value={open ? query : (selected?.label ?? '')}
        placeholder={selected && !open ? selected.label : placeholder}
        aria-label={ariaLabel}
        {...(label ? { label } : {})}
        {...(invalid ? { invalid: true } : {})}
        {...(disabled ? { disabled: true } : {})}
        onMudFocus={() => setOpen(true)}
        onMudInput={(event: CustomEvent<{ value: string }>) => {
          setQuery(event.detail.value);
          setHighlight(0);
          setOpen(true);
        }}
      />
      {open && !disabled ? (
        <div className="simsm-sites__picker-panel" role="group" aria-label={ariaLabel}>
          {shown.length === 0 ? (
            <p className="simsm-sites__picker-empty">
              {optionsUnavailable ? t('common.load_error') : t('search_select.no_results')}
            </p>
          ) : null}
          {shown.map((option, index) => (
            <MudButton
              shape="circular"
              key={option.value}
              appearance="text"
              variant="strict"
              size="md"
              className={
                index === highlight
                  ? 'simsm-sites__picker-option simsm-sites__picker-option--active'
                  : 'simsm-sites__picker-option'
              }
              label={option.label}
              // Keeping focus on the input is what stops the pick from
              // reopening the panel: without it the option takes focus, the
              // input takes it back when the panel unmounts, and mudFocus fires.
              onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
              onClick={() => choose(option)}
            >
              <span className="simsm-sites__picker-label">{option.label}</span>
              {option.meta ? <span className="simsm-sites__picker-meta">{option.meta}</span> : null}
            </MudButton>
          ))}
          {overflow > 0 ? (
            <p className="simsm-sites__picker-empty">
              {t('search_select.more', { count: overflow })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
